import { css } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { tokenize } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Collapse, type CollapseProps, Space, Tag, Typography, theme } from 'antd';
import type React from 'react';
import { useMemo } from 'react';
import type {
  ParameterSelection_DoctorSheetFragment,
  ParameterSelection_RequestableParameterFragment,
  ParameterToAddInput,
} from '../../../../graphql/generated/graphql.ts';
import type { SheetData } from '../../../../graphql/sheet';
import type { SelectedParamInfo } from '../Sheets.tsx';
import { SheetGroupCard } from './SheetGroupCard.tsx';
import { countSheetDataMatches } from './sheetSearch.ts';

export const SheetAccordion: React.FC<{
  sheet: ParameterSelection_DoctorSheetFragment;
  sheetParamLookup: Record<string, ParameterSelection_RequestableParameterFragment | undefined>;
  selectedParamInfo: SelectedParamInfo;
  onAdd: (inputs: ParameterToAddInput[]) => void;
  onRemove: (paramId: string) => void;
  onInfo: (param: ParameterSelection_RequestableParameterFragment) => void;
  reorder: boolean;
  flipParamNames: boolean;
  highlight: string;
}> = ({ sheet, sheetParamLookup, selectedParamInfo, onAdd, onRemove, onInfo, reorder, flipParamNames, highlight }) => {
  const { token } = theme.useToken();

  // Same tokenization Markable uses, so the counted matches are exactly the highlighted ones.
  const tokens = useMemo(() => tokenize(highlight), [highlight]);

  const items: CollapseProps['items'] = useMemo(
    () =>
      sheet.data.map((sheetData: SheetData, index: number) => {
        const matchCount = countSheetDataMatches(sheetData, tokens);

        return {
          key: index,
          label: highlight ? <Markable tokens={highlight}>{sheetData.name}</Markable> : sheetData.name,
          extra:
            matchCount > 0 || sheet.labId ? (
              <Space size="small">
                {matchCount > 0 && <Tag>{matchCount === 1 ? '1 Treffer' : `${matchCount} Treffer`}</Tag>}
                {sheet.labId && <Typography.Text type="secondary">{sheet.lab?.name}</Typography.Text>}
              </Space>
            ) : undefined,
          children: sheetData.groups.map((group, index) => (
            <SheetGroupCard
              key={index}
              sheet={sheetData}
              group={group}
              sheetParamLookup={sheetParamLookup}
              selectedParamInfo={selectedParamInfo}
              onAdd={onAdd}
              onRemove={onRemove}
              onInfo={onInfo}
              reorder={reorder}
              flipParamNames={flipParamNames}
              highlight={highlight}
            />
          )),
        };
      }) ?? [],
    [flipParamNames, highlight, onAdd, onInfo, onRemove, reorder, selectedParamInfo, sheet, sheetParamLookup, tokens]
  );

  return (
    <Collapse
      // destroyInactivePanel do not destroy to avoid re-rendering the DOM
      size="small"
      accordion
      items={items}
      className={css`
        background: transparent;

        .ant-collapse-title {
          font-weight: 600;
        }

        /* the header is the toggle — same hover fill as the profile cards */
        && > .ant-collapse-item > .ant-collapse-header {
          transition: background-color ${token.motionDurationFast};

          &:hover {
            background: ${token.controlItemBgHover};
          }
        }

        /* antd's default colorBorder is too heavy here — match ProfileCard's idle border.
           The content element is .ant-collapse-panel in antd 6 (.ant-collapse-content is v5),
           and && raises specificity so these win regardless of style insertion order. */
        &&,
        && > .ant-collapse-item,
        && .ant-collapse-panel {
          border-color: ${token.colorBorderSecondary};
        }

        margin-bottom: ${token.marginSM}px;
      `}
    />
  );
};
